import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import Header from "../../Header";
import { useMediaQuery, SwipeableDrawer } from "@mui/material";
import "./NavDrawer.css";

const drawerWidth = 230;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

function NavDrawer({ menuItems, mobileViewMenuItems }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = React.useState(!isMobile);

  React.useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  const handleDrawer = () => setOpen(!open);

  const handleTabClick = (path) => {
    if (!path || location.pathname === path) return;

    if (path.includes('/learner-practices')) {
      sessionStorage.removeItem('MentorPracticeselection');
      sessionStorage.removeItem('MentorPracticeshowTable');
    }
    if (path.includes('/learner-reviews')) {
      sessionStorage.removeItem('MentorReviewselection');
      sessionStorage.removeItem('MentorReviewshowTable');
    }
    navigate(path);
    if (isMobile) {
      setOpen(false);
    }
  };

  const userRole = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null")?.role;
    } catch {
      return null;
    }
  }, []);

  const currentPath = location.pathname;

  const renderDrawerContentForDestop = (
    <List>
      {menuItems?.map((item) => {
        const isDailyReportActive = currentPath.includes("/daily-report") && item.path?.includes("/daily-report");

        const isLabReportActive =
          (userRole === "admin" || userRole === "super_admin") &&
          (currentPath === "/admin/lab-report/module" ||
            currentPath === "/admin/lab-report/topic") &&
          item.path === "/admin/lab-report";

        const isMentorReviewActive = currentPath.includes("/learner-reviews") && item.path?.includes("/learner-reviews");
        const isLearnerPracticesActive = currentPath.includes("/learner-practices") && item.path?.includes("/learner-practices");
        const isLearnerAttendanceActive = (currentPath.includes("/learner-attendance") || currentPath.includes("/daily-report/attendance") || currentPath.includes("/attendance")) && item.path?.includes("/learner-attendance");

        const isActive = !item.disabled && (currentPath === item.path || isDailyReportActive || isLabReportActive || isMentorReviewActive || isLearnerPracticesActive || isLearnerAttendanceActive);

        return (
          <ListItem key={item.label} disablePadding sx={{ display: "block" }}>
            <Tooltip title={!open ? item.label : ""} placement="right" arrow>
              <ListItemButton
                className={`nav-item ${isActive ? "active" : ""} ${item.disabled ? "disabled" : ""}`}
                onClick={item.disabled ? undefined : () => handleTabClick(item.path)}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : "auto",
                    justifyContent: "center",
                    color: isActive ? "#fff" : "inherit",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </Tooltip>
          </ListItem>
        );
      })}
    </List>
  );

  const renderDrawerContentForMobile = (
    <List>
      {mobileViewMenuItems?.map((item) => {
        const isDailyReportActive = currentPath.includes("/daily-report") && item.path?.includes("/daily-report");

        const isMentorReviewActive = currentPath.includes("/learner-reviews") && item.path?.includes("/learner-reviews");
        const isLearnerPracticesActive = currentPath.includes("/learner-practices") && item.path?.includes("/learner-practices");
        const isLearnerAttendanceActive = (currentPath.includes("/learner-attendance") || currentPath.includes("/daily-report/attendance") || currentPath.includes("/attendance")) && item.path?.includes("/learner-attendance");

        const isActive = !item.disabled && (currentPath === item.path || isDailyReportActive || isMentorReviewActive || isLearnerPracticesActive || isLearnerAttendanceActive);

        return (
          <ListItem key={item.label} disablePadding sx={{ display: "block" }}>
            <Tooltip title={!open ? item.label : ""} placement="right" arrow>
              <ListItemButton
                className={`nav-item ${isActive ? "active" : ""} ${item.disabled ? "disabled" : ""}`}
                onClick={item.disabled ? undefined : () => handleTabClick(item.path)}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : "auto",
                    justifyContent: "center",
                    color: isActive ? "#fff" : "inherit",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </Tooltip>
          </ListItem>
        );
      })}
    </List>
  );

  return (
    <>
      <Header handleDrawer={handleDrawer} />
      <Box className="Box1" sx={{ display: "flex" }}>
        {/* Mobile Drawer (Swipeable) */}
        {isMobile && (userRole !== "admin" || userRole !== "super_admin") ? (
          <SwipeableDrawer
            anchor="left"
            open={open}
            onClose={() => setOpen(false)}
            onOpen={() => setOpen(true)}
          >
            {renderDrawerContentForMobile}
          </SwipeableDrawer>
        ) : (
          <Drawer variant="permanent" open={open}>
            {renderDrawerContentForDestop}
          </Drawer>
        )}
        <Box component="main" sx={{ flexGrow: 1, p: 3, minWidth: 0, maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }} className="outlet-box">
          <Outlet />
        </Box>
      </Box>
    </>
  );
}

export default NavDrawer;
